import styled from 'styled-components';

import { ImageHeader, WhatsAppContentDefinition } from 'modules/graphql/types';

import { getIsEditMode } from 'modules/templateEditor/common';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { EmptyInput, ReadOnlyField } from 'modules/templateEditor/EditEmbedded/utils';
import { FormField } from 'ui/formField';
import { OpenNewWindow16 } from 'ui/icons';
import { spacing } from 'ui/theme';
import { IS_VALID_URL } from 'utils/validationRegex';
import { UrlInputField } from '../../common/components';
import { displayContent } from '../../common/content';
import { constructErrorInlineText } from '../../common/ErrorInlineText';
import { FieldNames, MediumDataKey } from '../../common/types';
import { constructFieldErrors } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { StyledLink } from './components/styles';

const header: MediumDataKey = 'header';
const headerMedia: MediumDataKey = 'header.mediaUrl';

const StyledFormField = styled(FormField)`
  margin-top: ${spacing.plusHalf};
`;

export const ImageHeaderField = () => {
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { localeContentWarnings, localeContentErrors } = useErrorValidationContext();
  const whatsAppData = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  // @ts-ignore
  const value = whatsAppData.header?.mediaUrl || '';

  const { hasWarning: hasWhatsAppMediaUrlInputWarning } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.HEADER,
  });

  const whatsAppMediaUrlInputError = constructErrorInlineText({
    condition: isEditMode,
    hasWarning: hasWhatsAppMediaUrlInputWarning,
  });

  const handleMediaUrlChange = (newImageUrl: string) => {
    setCurrentMediumField(header, { mediaUrl: newImageUrl });
  };

  const isInputUrl = IS_VALID_URL.test(value);

  const { view } = useQueryParamContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  const ReadOnlyValue = () =>
    isInputUrl ? (
      <StyledLink href={value} target="_blank">
        <span>{value}</span>
        <OpenNewWindow16 isColorInherited />
      </StyledLink>
    ) : (
      <ReadOnlyField>{value}</ReadOnlyField>
    );

  return (
    <StyledFormField
      data-test="whats-app-image-header-field"
      isHorizontalLayout
      helpText={
        isEditModeVisible
          ? 'Supports JPEG or PNG less than 16MB and a recommended resolution of at least 640x360 pixels for optimal display.'
          : ''
      }
      labelText={SETTINGS_DISPLAY_CONTENT.imageHeader}
      hasWarning={hasWhatsAppMediaUrlInputWarning}
      warningText={whatsAppMediaUrlInputError}
    >
      {isEditModeVisible ? (
        <UrlInputField
          id={header}
          inputValue={(whatsAppData.header as ImageHeader)?.mediaUrl}
          onChangeHandler={handleMediaUrlChange}
          placeholderText={displayContent.placeholderMediaUrl}
          mediumDataKey={headerMedia}
        />
      ) : (
        <>
          {value ? (
            <ReadOnlyValue />
          ) : (
            <ReadOnlyField>
              <EmptyInput />
            </ReadOnlyField>
          )}
        </>
      )}
    </StyledFormField>
  );
};
